import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { fetchAccountESFeeds, exportESFeeds } from './thunks';
import {
	ESFeedResponse,
	ESFeed,
	ESFeedAggregateResponse,
} from 'src/types/dataTypes';

export interface DetectionFilter {
	fieldName: string;
	value: string[];
	operation: string;
}

export interface DetectionsState {
	tableData: ESFeed[] | null;
	loading: boolean;
	error: string | null;
	page: number;
	pageSize: number;
	totalCount: number;
	count: number;
	searchText: string;
	searchField: string;
	sort: string;
	sortDir: string;
	dateRangeStart: string | null;
	dateRangeEnd: string | null;
	filters: DetectionFilter[];
	filtersAggregation: ESFeedAggregateResponse[] | null;
	toast: {
		message: string;
		type: 'info' | 'success' | 'error' | 'warning' | 'default';
	};
}

const initialState: DetectionsState = {
	tableData: null,
	loading: false,
	error: null,
	page: 1,
	pageSize: 25,
	totalCount: 0,
	count: 0,
	searchText: '',
	searchField: '',
	sort: 'lastUpdated',
	sortDir: 'desc',
	dateRangeStart: DateTime.local()
		.minus({ days: 30 })
		.startOf('day')
		.toISO(),
	dateRangeEnd: DateTime.local().toISO(),
	filters: [],
	filtersAggregation: null,
	toast: {
		message: '',
		type: 'success',
	},
};

const detectionsSlice = createSlice({
	name: 'detections',
	initialState,
	reducers: {
		resetDetectionState: () => initialState,
		updatePage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		updatePageSize: (state, action: PayloadAction<number>) => {
			state.pageSize = action.payload;
		},
		updateDateRange: (
			state,
			action: PayloadAction<{
				start: string | null;
				end: string | null;
			}>,
		) => {
			state.dateRangeStart = action.payload.start;
			state.dateRangeEnd = action.payload.end;
		},
		updateSearchTextAndField: (
			state,
			action: PayloadAction<{ text: string; field: string }>,
		) => {
			state.searchText = action.payload.text;
			state.searchField = action.payload.field;
		},
		updateSort: (
			state,
			action: PayloadAction<{ sort: string; sortDir: string }>,
		) => {
			state.sort = action.payload.sort;
			state.sortDir = action.payload.sortDir;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				message: string;
				type: 'info' | 'success' | 'error' | 'warning' | 'default';
			}>,
		) => {
			state.toast = { ...state.toast, ...action.payload };
		},
		updateFilters: (state, action: PayloadAction<DetectionFilter[]>) => {
			state.filters = action.payload;
		},
		updateFiltersAggregation: (
			state,
			action: PayloadAction<ESFeedAggregateResponse[] | null>,
		) => {
			state.filtersAggregation = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAccountESFeeds.pending, (state) => {
				state.loading = true;
				state.error = null;
				state.tableData = null;
			})
			.addCase(
				fetchAccountESFeeds.fulfilled,
				(state, action: PayloadAction<ESFeedResponse>) => {
					state.loading = false;
					state.tableData = action.payload.data;
					state.error = null;
					state.totalCount = action.payload.totalCount;
					state.count = action.payload.count;
				},
			)
			.addCase(fetchAccountESFeeds.rejected, (state, action) => {
				// Skip state update if request was aborted due to tab/page switch
				if (action.meta.aborted) {
					state.loading = false;
					return;
				}
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(exportESFeeds.pending, (state) => {
				state.loading = true;
			})
			.addCase(exportESFeeds.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(exportESFeeds.rejected, (state) => {
				state.loading = false;
			});
	},
});

export const {
	resetDetectionState,
	updatePageSize,
	updatePage,
	updateDateRange,
	updateSearchTextAndField,
	updateSort,
	pushToast,
	updateFilters,
	updateFiltersAggregation,
} = detectionsSlice.actions;
export default detectionsSlice.reducer;
